import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import warningIcon from '../../assets/confirmation-warning.svg';
import closeIcon from '../../assets/confirmation-close.svg';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  secondaryMessage?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  appearance?: 'standard' | 'centered';
  icon?: ReactNode;
  closeLabel?: string;
  confirmClassName?: string;
  messageClassName?: string;
  dialogClassName?: string;
  isPending?: boolean;
  errorMessage?: string | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  returnFocus?: () => void;
}

const centeredAction = 'inline-flex h-11 w-full shrink-0 items-center justify-center whitespace-nowrap rounded-lg border text-[15px] font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:opacity-50';

/** Shared confirmation behavior with the existing standard and centered Figma presentations. */
export default function ConfirmDialog({
  open, title, message, secondaryMessage, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  variant = 'default', appearance = 'standard', icon, closeLabel, confirmClassName,
  messageClassName = '', dialogClassName = '', isPending = false, errorMessage, onConfirm, onCancel, returnFocus,
}: ConfirmDialogProps) {
  const restoreFocusRef = useRef(returnFocus);
  useEffect(() => { restoreFocusRef.current = returnFocus; }, [returnFocus]);
  const titleId = useId();
  const descriptionId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const overlay = overlayRef.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Keep the original trigger when Strict Mode replays setup after focusing Cancel.
    if (previousFocus && !overlay?.contains(previousFocus)) previousFocusRef.current = previousFocus;
    cancelRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const siblings = Array.from(document.body.children)
      .filter((child): child is HTMLElement => child instanceof HTMLElement && child !== overlayRef.current)
      .map((element) => ({ element, inert: element.inert }));
    siblings.forEach(({ element }) => { element.inert = true; });
    return () => {
      document.body.style.overflow = overflow;
      siblings.forEach(({ element, inert }) => { element.inert = inert; });
      // Wait for React to finish removing the row before selecting a focus target.
      queueMicrotask(() => {
        // Strict Mode replays cleanup while the dialog is still mounted.
        if (!overlay?.isConnected) {
          if (restoreFocusRef.current) restoreFocusRef.current();
          else if (previousFocusRef.current?.isConnected) previousFocusRef.current.focus();
        }
      });
    };
  }, [open]);

  useEffect(() => {
    // Disabled buttons can lose focus in the browser while the request is running.
    if (!open) return;
    if (isPending) dialogRef.current?.focus();
    else if (document.activeElement === dialogRef.current) cancelRef.current?.focus();
  }, [open, isPending]);

  if (!open) return null;
  const centered = appearance === 'centered';
  const danger = variant === 'danger';
  const messageClasses = `w-full min-w-0 [overflow-wrap:anywhere] text-center text-[16px] leading-6 tracking-[-0.16px] text-[#273141] ${messageClassName}`;

  return createPortal(
    <div ref={overlayRef} className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" tabIndex={-1} aria-label="Dismiss confirmation" disabled={isPending} onClick={onCancel}
        className={`fixed inset-0 ${centered ? 'bg-white/70 backdrop-blur-sm dark:bg-black/70' : 'bg-black/40'}`} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={isPending} tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            if (!isPending) onCancel();
          }
          if (event.key !== 'Tab') return;
          const buttons = Array.from(dialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (!first) { event.preventDefault(); dialogRef.current?.focus(); }
          else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }}
        className={centered
          ? `relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-[652px] flex-col items-center gap-[25px] overflow-x-hidden overflow-y-auto overscroll-contain rounded-[11px] border-4 ${danger ? 'border-[#c62828]' : 'border-brand'} bg-white px-6 py-6 text-center sm:py-[46px] ${dialogClassName}`
          : 'card relative z-10 max-h-[calc(100vh-2rem)] w-full max-w-md overflow-x-hidden overflow-y-auto overscroll-contain p-6'}>
        {closeLabel && <button type="button" aria-label={closeLabel} onClick={onCancel} disabled={isPending} className="absolute right-0 top-0 flex h-[43px] w-[37px] items-start justify-end pr-[5px] pt-[3px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:opacity-50">
          <img src={closeIcon} width="19" height="25" alt="" />
        </button>}
        {centered ? <>
          {icon ?? <img src={warningIcon} width="36" height="36" alt="" />}
          <h3 id={titleId} className="w-full max-w-[552px] break-words text-[22px] font-medium leading-[33px] tracking-[-0.22px] text-[#273141]">{title}</h3>
          <p id={descriptionId} className={messageClasses}>{message}</p>
          {secondaryMessage && <p className={messageClasses}>{secondaryMessage}</p>}
        </> : <div className="flex items-start gap-3">
          {danger && <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30"><AlertTriangle aria-hidden="true" className="h-5 w-5 text-red-600 dark:text-red-400" /></div>}
          <div className="min-w-0">
            <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p id={descriptionId} className="mt-1 break-words text-sm text-gray-500 dark:text-gray-400">{message}</p>
            {secondaryMessage && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{secondaryMessage}</p>}
          </div>
        </div>}
        {errorMessage && <p role="alert" className={centered ? 'w-full min-w-0 [overflow-wrap:anywhere] text-sm text-[#c62828]' : 'mt-4 text-sm text-red-600 dark:text-red-400'}>{errorMessage}</p>}
        <div className={centered ? 'flex w-full max-w-[428px] flex-col justify-center gap-3 sm:flex-row sm:gap-[58px]' : 'mt-6 flex justify-end gap-2'}>
          <button ref={cancelRef} type="button" onClick={onCancel} disabled={isPending} className={centered ? `${centeredAction} px-6 sm:w-[180px] ${danger ? 'border-[#273141] bg-white text-[#273141] hover:bg-gray-50' : 'border-[#3a149e] bg-white text-[#3a149e] hover:bg-brand-tint'}` : 'btn-secondary'}>{cancelLabel}</button>
          <button type="button" onClick={onConfirm} disabled={isPending} className={confirmClassName ?? (centered ? `${centeredAction} ${danger ? 'sm:w-[174px] border-[#c62828] bg-white px-[23px] text-[#c62828] hover:bg-red-50' : 'sm:w-[198px] border-[#3a149e] bg-[#3a149e] px-6 text-white hover:bg-brand'}` : danger ? 'btn-danger' : 'btn-primary')}>{confirmLabel}</button>
        </div>
      </div>
    </div>, document.body,
  );
}
